import type { JSONString } from '@fragment/types/json';

export const isSafeInteger = (n: bigint | number): boolean => {
  return n >= Number.MIN_SAFE_INTEGER && n <= Number.MAX_SAFE_INTEGER;
};

export type SubstBigInt<T> = T extends Function
  ? T
  : T extends Array<infer U>
  ? Array<SubstBigInt<U>>
  : T extends object
  ? _DeepSubstBigIntObject<T>
  : T extends bigint
  ? number | string
  : T;
export declare type _DeepSubstBigIntObject<T> = {
  [P in keyof T]: SubstBigInt<T[P]>;
};

export const bigIntToNumberOrStringReplacer = (
  key: string,
  value: unknown
): any => {
  if (typeof value === 'bigint') {
    if (isSafeInteger(value)) {
      return Number(value);
    }
    return value.toString();
  }
  return value;
};

/**
 * Convert a value to a JSON string, converting bigints to numbers or strings.
 */
export const stringify = (value: unknown, space?: number) =>
  JSON.stringify(value, bigIntToNumberOrStringReplacer, space) as JSONString;

export function maybeBigInt(
  value: Parameters<typeof BigInt>[0] | null
): bigint | null;
export function maybeBigInt(
  value?: Parameters<typeof BigInt>[0]
): bigint | undefined;
export function maybeBigInt(
  value?: Parameters<typeof BigInt>[0] | null
): bigint | null | undefined;
export function maybeBigInt(
  value?: Parameters<typeof BigInt>[0] | null
): bigint | null | undefined {
  if (value === null) {
    return null;
  }
  if (value === undefined) {
    return undefined;
  }
  return BigInt(value);
}

// This is BigInt(2) ** BigInt(120) - BigInt(1)
export const int96MaxValue = 1329227995784915872903807060280344575n;
export const int96MinValue = -int96MaxValue;

export const validateInt96ValueRange = (value: bigint) => {
  if (value < int96MinValue)
    throw new TypeError(
      `Values less than ${int96MinValue} are currently unsupported`
    );
  if (value > int96MaxValue)
    throw new TypeError(
      `Values greater than ${int96MaxValue} are currently unsupported`
    );
};

export const isInt96Value = (value: bigint): boolean =>
  value >= int96MinValue && value <= int96MaxValue;

export function validateIsBigInt(value: bigint): asserts value is bigint {
  try {
    BigInt(value);
  } catch (err: unknown) {
    throw new TypeError(`Expected bigint. Got: ${value}`);
  }
}

// TODO: This can be changed to a type guard function (e.g. `value is bigint`)
export const isBigInt = (value: unknown) => {
  try {
    BigInt(value as bigint);
    return true;
  } catch (err: unknown) {
    return false;
  }
};

export const bigIntToString = (value: bigint): string => {
  validateIsBigInt(value);
  validateInt96ValueRange(value);
  return value.toString();
};

export const toBigInt = (value: unknown): bigint => {
  const bigIntValue = BigInt(value as bigint);
  validateInt96ValueRange(bigIntValue);
  return bigIntValue;
};
